import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';

import Tooltip from 'components/Tooltip';
import IconButton from 'components/IconButton';
import Icon from 'components/Icon';
import { Opportunity } from 'components/opportunity-page';
import Link from 'components/Link';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from './OpportunityPreview.module.scss';

const OpportunityPreview: React.FC<{ className?: string; floatingButtonsClassName?: string; newTab?: boolean }> = ({
  className,
  floatingButtonsClassName,
  newTab = true,
}) => {
  const { isNonOnboardedUser } = useAuth();
  const [t] = useTranslation('search.results');
  const [canShowMatchesBanner, setCanShowMatchesBanner] = useState(true);
  const {
    opportunity: { touchpointable_type, is_in_current_matches, slug },
  } = useOpportunity();
  const publicPageUrl = getOpportunityPublicPagePathname(touchpointable_type, slug);

  const showMatchesBanner = is_in_current_matches && canShowMatchesBanner;

  const matchBanner = useMemo(() => {
    if (isNonOnboardedUser) return null;

    return showMatchesBanner ? (
      <div className={styles['match-banner']}>
        <div className={styles.left}>
          <Icon iconName="icon_magic-wand" size="large" />
          <p>{t('match-banner.title', { opportunityType: touchpointable_type.toLowerCase() })}</p>
        </div>
        <div className={styles.right}>
          <Link href={PAGE_ROUTES.CANDIDATE_MATCHES} prefetch>
            <button className={styles['matches-link']}>{t('match-banner.cta')}</button>
          </Link>
          <IconButton iconName="icon_close" className={styles.cancel} onClick={() => setCanShowMatchesBanner(false)} />
        </div>
      </div>
    ) : null;
  }, [showMatchesBanner, touchpointable_type]);

  return (
    <>
      {newTab && (
        <div className={cx(styles['launch-icon-container'], { [styles['below-matches']]: showMatchesBanner })}>
          <Tooltip content={t('opportunity-page.tooltip')} color="dark" position="left" isRounded>
            <a href={publicPageUrl} target="_blank" rel="noreferrer">
              <IconButton iconName="icon_launch" size="auto" className={styles['launch-icon']} />
            </a>
          </Tooltip>
        </div>
      )}
      <div className={cx(styles.container, className)}>
        {matchBanner}
        <Opportunity floatingButtonsClassName={floatingButtonsClassName} />
      </div>
    </>
  );
};

OpportunityPreview.displayName = 'OpportunityPreview';

export default OpportunityPreview;
